import React, { createContext, useContext, useMemo, useState } from 'react';
import type { FC } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type RendererActions from '@atlaskit/renderer/actions';

export type RendererActionsContextType = {
	rendererActions: RendererActions | null;
	pageId?: string;
	pageAdf?: DocNode;
};

export type RendererActionsDispatchContextType = {
	setRendererActions: (rendererActions?: RendererActions, pageId?: string) => void;
	setPageAdf: (pageAdf: DocNode | undefined) => void;
};

export const RendererActionsContext = createContext<RendererActionsContextType>({
	rendererActions: null,
	pageId: undefined,
	pageAdf: undefined,
});
RendererActionsContext.displayName = 'RendererActionsContext';

export const RendererActionsDispatchContext = createContext<RendererActionsDispatchContextType>({
	setRendererActions: () => {},
	setPageAdf: () => {},
});
RendererActionsDispatchContext.displayName = 'RendererActionsDispatchContext';

type RendererActionsProviderProps = {
	children: JSX.Element;
};

export const RendererActionsProvider: FC<RendererActionsProviderProps> = ({ children }) => {
	const [rendererActions, setRendererActions] = useState<any>(null);
	const [pageId, setPageId] = useState<string | undefined>(undefined);
	const [pageAdf, setPageAdf] = useState<DocNode | undefined>(undefined);

	const providerValue = useMemo(
		() => ({
			rendererActions,
			pageId,
			pageAdf,
		}),
		[rendererActions, pageId, pageAdf],
	);

	const dispatchProviderValue = useMemo(
		() => ({
			setRendererActions: (actions?: any, newPageId?: string) => {
				setRendererActions(actions);
				setPageId(newPageId);
			},
			setPageAdf: (newPageAdf: DocNode | undefined) => {
				setPageAdf(newPageAdf);
			},
		}),
		[],
	);

	return (
		<RendererActionsContext.Provider value={providerValue}>
			<RendererActionsDispatchContext.Provider value={dispatchProviderValue}>
				{children}
			</RendererActionsDispatchContext.Provider>
		</RendererActionsContext.Provider>
	);
};

export const useRendererActions = () => {
	return useContext(RendererActionsContext);
};

export const useRendererActionsDispatch = () => {
	return useContext(RendererActionsDispatchContext);
};
